<template>
  <BFormGroup class="custom-multi-select mb-0">
    <div ref="root" class="multi-select-shell">
      <div
        class="field-shell"
        :class="{ open, focused, 'has-value': selected.length }"
      >
        <BButton
          type="button"
          variant="light"
          class="multi-select-trigger"
          :class="{ open, 'has-value': selected.length }"
          @focus="focused = true"
          @blur="focused = false"
          @click.stop="toggleOpen"
        >
          <div class="trigger-content">
            <template v-if="selected.length">
              <div class="selected-summary">
                <div class="selected-chips">
                  <BBadge
                    v-for="item in selectedPreview"
                    :key="item"
                    pill
                    class="selected-chip"
                  >
                    {{ item }}
                  </BBadge>
                  <span
                    v-if="selected.length > previewLimit"
                    class="selected-more"
                  >
                    +{{ selected.length - previewLimit }}
                  </span>
                </div>
              </div>
            </template>
          </div>
          <span class="trigger-caret" :class="{ open }">&#9662;</span>
        </BButton>
        <span class="field-label">{{ selectLabel }}</span>
      </div>

      <transition name="fade">
        <div v-if="open" class="multi-select-panel" @click.stop>
          <div class="panel-header">
            <div class="count-info">
              <div class="count-display">
                <span class="count">{{ selected.length }}</span>
                <span>Selected</span>
              </div>
              <BButton
                variant="link"
                class="clear-btn p-0"
                type="button"
                @click.stop="clearSelection"
              >
                Clear All
              </BButton>
            </div>
            <input
              v-model="search"
              class="search-input"
              placeholder="Search"
              autocomplete="off"
              @keydown.enter.prevent="handleTag"
            />
          </div>

          <div class="options-list">
            <template v-if="filteredOptions.length">
              <BFormCheckbox
                v-for="option in filteredOptions"
                :key="option"
                class="option-item"
                :class="{ selected: selected.includes(option) }"
                :model-value="selected.includes(option)"
                @update:model-value="toggleOption(option, $event)"
              >
                {{ option }}
              </BFormCheckbox>
            </template>
            <div v-else-if="showNoOptionsFlag" class="no-options">
              No options found
            </div>
          </div>

          <div v-if="tagFlag && search.trim()" class="add-tag-row">
            <BButton
              type="button"
              size="sm"
              variant="outline-primary"
              @click.stop="handleTag"
            >
              Add "{{ search.trim() }}"
            </BButton>
          </div>
        </div>
      </transition>
    </div>
  </BFormGroup>
</template>

<script>
import { BBadge, BButton, BFormCheckbox, BFormGroup } from "bootstrap-vue-next"

export default {
  name: "custom-multi-select",
  components: {
    BBadge,
    BButton,
    BFormCheckbox,
    BFormGroup,
  },
  props: {
    selectOptions: {
      type: Array,
      default: () => [],
    },
    selectLabel: {
      type: String,
      default: "Search or select experience",
    },
    selectedList: {
      type: Array,
      default: () => [],
    },
    tagFlag: {
      type: Boolean,
      default: false,
    },
    showNoOptionsFlag: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      focused: false,
      search: "",
      selected: [],
      internalOptions: [],
      previewLimit: 2,
    }
  },
  computed: {
    filteredOptions() {
      const query = this.search.trim().toLowerCase()
      if (!query) {
        return this.internalOptions
      }

      return this.internalOptions.filter((option) =>
        option.toLowerCase().includes(query)
      )
    },
    selectedPreview() {
      return this.selected.slice(0, this.previewLimit)
    },
  },
  watch: {
    selectedList: {
      immediate: true,
      handler(newValue) {
        this.selected = [...new Set(newValue || [])]
      },
    },
    selectOptions: {
      immediate: true,
      handler(newValue) {
        this.internalOptions = [...new Set(newValue || [])]
      },
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick)
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick)
  },
  methods: {
    emitSelection() {
      this.$emit("update-selected", [...this.selected])
    },
    toggleOpen() {
      this.open = !this.open
    },
    handleDocumentClick(event) {
      if (!this.$refs.root) {
        return
      }

      if (!this.$refs.root.contains(event.target)) {
        this.open = false
      }
    },
    toggleOption(option, checked) {
      if (checked) {
        if (!this.selected.includes(option)) {
          this.selected = [...this.selected, option]
        }
      } else {
        this.selected = this.selected.filter((item) => item !== option)
      }
      this.emitSelection()
    },
    handleTag() {
      const tag = this.search.trim()
      if (!this.tagFlag || !tag) {
        return
      }

      if (!this.internalOptions.includes(tag)) {
        this.internalOptions = [...this.internalOptions, tag]
      }

      if (!this.selected.includes(tag)) {
        this.selected = [...this.selected, tag]
        this.emitSelection()
      }

      this.search = ""
    },
    clearSelection() {
      this.selected = []
      this.emitSelection()
    },
  },
}
</script>

<style scoped>
.custom-multi-select {
  width: 100%;
}

.multi-select-shell {
  position: relative;
  width: 100%;
  border: none !important;
}

.field-shell {
  position: relative;
  width: 100%;
  border: none !important;
}

.field-label {
  position: absolute;
  z-index: 99;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 8px;
  background: #fff;
  color: var(--Gray-600, #525e77);
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  pointer-events: none;
  transition: top 0.15s ease, transform 0.15s ease, color 0.15s ease,
    font-size 0.15s ease;
}

.field-shell.open .field-label,
.field-shell.focused .field-label,
.field-shell.has-value .field-label {
  top: 0;
  transform: translateY(-50%);
  font-size: 12px;
}

.field-shell.open .field-label,
.field-shell.focused .field-label {
  color: #0095e2;
  z-index: 999;
}

.field-shell.has-value .field-label {
  color: var(--Gray-600, #525e77);
}

.multi-select-trigger {
  width: 100%;
  padding: 10px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--White, #fff);
  color: #343946;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.08);
  text-align: left;
  position: relative;
  border: 1px solid var(--Gray-300, #b0b9c9);

  &:hover,
  &:focus {
    border: 2px solid var(--Gray-300, #525e77) !important;
    background: unset;
  }
}

.multi-select-trigger.open {
  border: 2px solid var(--Gray-300, #0095e2) !important;

  .trigger-caret {
    color: #0095e2;
  }
}

.trigger-content {
  flex: 1;
  min-width: 0;
}

.selected-summary {
  display: block;
  width: 100%;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.selected-chip {
  background: #e0e0e0 !important;
  color: var(--Gray-900, #343946) !important;
  font-size: 16px;
  font-weight: 400;
  padding: 10px 16px;
  border-radius: 16px !important;
  text-transform: unset;
  font-weight: 400;
}

.selected-more,
.trigger-caret {
  color: var(--Gray-600, #525e77);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.trigger-caret {
  font-size: 25px !important;
  line-height: 1;
  transition: transform 0.2s ease;
}

.trigger-caret.open {
  transform: rotate(180deg);
}

.multi-select-panel {
  position: absolute;
  z-index: 100;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  background: #fff;
  border: 1px solid #c7d0dd;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 18px 30px rgba(17, 24, 39, 0.16);
  padding: 14px;
  max-height: 420px;
  overflow: auto;
}

.panel-header {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 12px;
}

.count-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #0095e2;
  font-size: 16px;
  font-weight: 500;
}

.count-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.count {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0095e2;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}

.clear-btn {
  font-weight: 600;
  color: #0095e2 !important;
  text-decoration: none;
}

.search-input {
  min-height: 58px;
  border-radius: 12px;
  border: 2px solid #b7bfd0;
  padding: 12px 16px;
  font-size: 16px;
  box-shadow: none;

  &:focus,
  &:focus-visible {
    border-color: #0095e2;
    outline: none;
  }
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  width: 100%;
  min-height: 64px;
  margin: 0;
  padding: 14px 16px;
  border-radius: 0;
  background: transparent;
  color: #343946;
  font-size: 18px;
  transition: background 0.15s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

.option-item.selected {
  background: #a9d9f7;
  color: #0095e2;

  &:hover,
  &:focus-within {
    background: var(--gfb-blue-brand-50, #c8ecff) !important;
  }
}

.option-item:hover,
.option-item:focus-within {
  background: var(--Gray-100, #eceef2);

  :deep(.form-check-input) {
    border: 2px solid #0095e2;
  }
}

.option-item :deep(.form-check-label:hover) {
  background: transparent !important;
}

.option-item :deep(.form-check-input) {
  width: 20px;
  height: 20px;
  border-radius: 2px;
  margin-left: 0;
  margin-right: 12px;
  border: 2px solid #525e77;
  background-color: transparent;
  margin-top: 0;
  box-shadow: none !important;
}

.option-item.selected :deep(.form-check-input) {
  border: 2px solid #0095e2;
  background-color: #0095e2;
}

.no-options {
  padding: 8px 0;
  color: var(--Gray-600, #525e77);
  font-size: 14px;
}

.add-tag-row {
  margin-top: 12px;
}

@media (max-width: 768px) {
  .field-label {
    font-size: 14px;
  }

  .multi-select-trigger {
    min-height: 60px;
    padding: 24px 14px 12px 14px;
  }

  .multi-select-panel {
    padding: 12px;
  }

  .selected-summary {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
